import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBoxesStacked,
  faChevronDown,
  faTag,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { TallyProductCatalog, type TallyProduct } from '../components/TallyProductCatalog'
import { whatsAppCtaProps } from '../lib/analytics'
import {
  getProductPrimaryImage,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/bra-daililing'
const PAGE_CATEGORY = 'Bra Daililing'
const SEARCH_TERM = 'daililing'

function resolveDaililingProducts(products: StorefrontProduct[]): TallyProduct[] {
  return products
    .filter((product) =>
      /\b(bra|bh)\b/i.test(product.name),
    )
    .map((product) => ({
      name: product.name,
      slug: product.slug,
      sku: product.sku,
      image: getProductPrimaryImage(product, { width: 760, format: 'webp', quality: 78 }),
    }))
}

const DROPIFY_REASONS = [
  {
    icon: faBolt,
    title: 'Pemesanan via WhatsApp',
    desc: 'Kirim model bra Daililing, jumlah lusin, dan kota tujuan. Admin akan memeriksa stok serta menyiapkan quotation.',
  },
  {
    icon: faBoxesStacked,
    title: 'Cek model BH 358 sekaligus',
    desc: 'Admin memeriksa ukuran dan warna yang tersedia setelah Anda mengirim daftar model atau foto referensi.',
  },
  {
    icon: faTag,
    title: 'Sample sebelum order besar',
    desc: 'Sample dihitung harga satuan ditambah ongkir. Gunakan untuk mengecek bahan, bentuk cup, jahitan, dan ukuran sebelum order grosir.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim ke toko, gudang, atau lokasi usaha',
    desc: 'Alamat toko, gudang, atau rumah sama-sama bisa dilayani lewat ekspedisi yang sesuai.',
  },
]

const BRA_DAILILING_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Apakah bra Daililing selalu tersedia?',
    a: 'Stok mengikuti produksi. Kirim model BH 358 atau foto yang diminati. Admin akan mengirim foto stok aktual, ukuran, dan warna yang tersedia.',
  },
  {
    q: 'Stok BH 358 cukup untuk pesanan besar sekali kirim?',
    a: 'Kirim jumlah lusin yang Anda rencanakan. Admin akan menjelaskan jumlah yang tersedia beserta pilihan ukuran dan warnanya.',
  },
  {
    q: 'Bagaimana cara memilih bra Daililing untuk toko?',
    a: 'Kirim kebutuhan ukuran dan warna untuk model BH 358 Daililing. Admin akan mengirim foto serta pilihan yang tersedia.',
  },
  {
    q: 'Perlu order ulang rutin untuk toko, bagaimana alurnya?',
    a: 'Chat admin dengan menyebut BH 358 dan jumlah restock. Karena modelnya sama, pengecekan stok dan harga berjalan lebih singkat dari pesanan pertama.',
  },
  {
    q: 'Berapa lama pengiriman bra Daililing dari Surabaya?',
    a: 'Pesanan dikirim dari Surabaya. Admin mengonfirmasi waktu penyiapan, lalu memberi estimasi perjalanan berdasarkan kota tujuan dan layanan ekspedisi.',
  },
]

export const BRA_DAILILING_FAQ: FaqItem[] = [
  {
    q: 'Model bra Daililing apa yang tersedia di Dropify?',
    a: 'Tersedia model BH 358 Daililing untuk pemakaian harian. Stok mengikuti produksi, jadi kirim foto atau nama model saat bertanya agar admin mengecek stok yang tepat.',
  },
  {
    q: 'Berapa harga dan minimum order bra Daililing?',
    a: 'MOQ bra Daililing umumnya 25 lusin dengan isi 12 pcs per lusin. Admin akan mencantumkan harga pada quotation setelah jumlah pesanan dipastikan.',
  },
  {
    q: 'Apakah ukuran dan warna bra Daililing bisa dicampur?',
    a: 'Bisa selama kombinasi ukuran dan warnanya tersedia. Sebutkan berapa pcs yang dibutuhkan untuk setiap ukuran atau warna. Admin akan mengecek kombinasinya, lalu mengonfirmasi susunan yang bisa disiapkan.',
  },
  {
    q: 'Bisakah memesan sampel bra Daililing lebih dahulu?',
    a: 'Bisa. Sampel dihitung dengan harga satuan ditambah ongkir. Anda dapat memeriksa bahan, bentuk cup, jahitan, elastisitas, label, dan ukuran secara langsung sebelum menentukan jumlah untuk pesanan grosir.',
  },
  {
    q: 'Bagaimana cara memesan bra Daililing di Dropify?',
    a: 'Lewat WhatsApp, sampaikan BH 358, jumlah lusin, kebutuhan ukuran dan warna, serta kota tujuan. Admin mengecek stok, menghitung harga dan ongkir, lalu memandu pembayaran hingga barang berangkat.',
  },
]

const THEME = {
  border: 'border-emerald-100',
  bg: 'bg-emerald-50',
  cardBg: 'bg-white',
  primaryText: 'text-emerald-700',
  hoverText: 'text-emerald-700',
  buttonBg: 'bg-emerald-700',
  buttonText: 'text-white',
  ring: 'focus:ring-emerald-100',
  placeholderBg: 'bg-[#f0fff8]',
}

export const Route = createFileRoute('/bra-daililing')({
  loader: async () => {
    const [config, categories, response, proofImages] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ search: SEARCH_TERM, per_page: 100 }).catch(() => null),
      getBrandProofImages(),
    ])

    return {
      config,
      categories,
      products: resolveDaililingProducts(response?.items ?? []),
      proofImages,
    }
  },
  head: ({ loaderData }) => {
    const title = brandTitle('Grosir Bra Daililing untuk Toko & Reseller')
    const description =
      'Grosir bra Daililing BH 358 dengan MOQ umumnya 25 lusin. Cek ukuran, warna, stok, dan quotation melalui WhatsApp.'
    const products = loaderData?.products ?? []
    const heroImage = products[0]?.image

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        ...(heroImage ? [{ property: 'og:image', content: heroImage }] : []),
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        ...(heroImage ? [{ name: 'twitter:image', content: heroImage }] : []),
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'Daililing', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Merek', path: '/merek' },
              { name: 'Bra Daililing', path: PAGE_PATH },
            ]),
          ),
        },
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema([...BRA_DAILILING_FAQ, ...BRA_DAILILING_TRUST_QUESTIONS])) },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Bra Daililing di Dropify',
            itemListElement: products.map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Thing',
                name: product.name,
                sku: product.sku,
                image: product.image,
                url: absoluteUrl(`/produk/${product.slug}`),
                brand: { '@type': 'Brand', name: 'Daililing' },
              },
            })),
          }),
        },
      ],
    }
  },
  component: BraDaililingRoute,
})

function BraDaililingRoute() {
  const { config, categories, products, proofImages } = Route.useLoaderData()
  return <BraDaililingLandingPage config={config} categories={categories} products={products} proofImages={proofImages} />
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  className,
}: {
  message: string
  label: string
  placement: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', { cta_placement: placement, lp_category: PAGE_CATEGORY })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">
      {children}
    </p>
  )
}

export function BraDaililingLandingPage({
  config = null,
  categories = [],
  products = [],
  proofImages = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  products?: TallyProduct[]
  proofImages?: string[]
}) {
  const heroProducts = products.slice(0, 4)
  const heroMessage =
    'Halo Dropify, saya mau cek bra Daililing untuk stok toko. Model yang diminati: ___. Kebutuhan: ___ lusin. Ukuran/warna: ___. Kota tujuan: ___. Bisa kirim stok aktual dan harganya?'

  return (
    <div className="min-h-screen bg-[#f0fff8] text-[#102b18]">
      <div className="bg-emerald-200 px-4 py-2 text-center text-xs font-black text-[#103d20] sm:text-sm">
        {products.length} model bra Daililing · MOQ 25 lusin · 1 lusin isi 12 pcs
      </div>

      <header className="border-b border-white/10 bg-[#103d20] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Periksa stok bra Daililing"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200/60 px-4 py-2.5 text-sm font-bold text-emerald-100 transition-colors hover:bg-emerald-200 hover:text-[#103d20]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#103d20] text-white">
          <div aria-hidden className="absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" />
          <div aria-hidden className="absolute -right-20 top-16 h-96 w-96 rounded-full border-[64px] border-emerald-200/5" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="inline-flex rounded-full bg-emerald-200 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#103d20]">Grosir bra Daililing</p>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
                Bra Daililing BH 358 untuk <span className="text-emerald-200">melengkapi pilihan di toko Anda.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-emerald-50/80 md:text-lg">
                Kirim model BH 358 ke admin. Kami akan mengecek ukuran, warna, stok, dan harga untuk pesanan mulai 25 lusin.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton
                  message={heroMessage}
                  label="Minta quotation bra Daililing"
                  placement="hero_primary"
                  className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
                />
                <a href="#model-bra-daililing" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">
                  Lihat {products.length} model
                  <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-5 text-sm text-emerald-100/70">Harga dan stok dikonfirmasi melalui quotation.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {heroProducts.map((product, index) => (
                <article
                  key={product.sku}
                  data-hero-product-visual
                  className={`overflow-hidden rounded-3xl bg-white p-3 text-[#103d20] shadow-2xl ${index % 2 === 1 ? 'translate-y-5' : '-translate-y-1'}`}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    width="560"
                    height="560"
                    fetchPriority={index < 2 ? 'high' : undefined}
                    decoding="async"
                    className="aspect-square w-full rounded-2xl object-cover"
                  />
                  <div className="px-2 pb-2 pt-3">
                    <span className="text-sm font-black">{product.name}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <BrandEasyOrder />

        <section id="model-bra-daililing" className="scroll-mt-6 bg-[#f0fff8] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
              <div className="max-w-3xl">
                <SectionLabel>{`${products.length} model yang tersedia`}</SectionLabel>
                <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pilih model bra Daililing yang ingin Anda cek</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">Klik foto untuk membuka detail produk. Saat menghubungi admin, kirim nama model dan jumlah yang dibutuhkan untuk pemeriksaan stok.</p>
              </div>
            </div>

            <TallyProductCatalog
              products={products}
              pageCategory={PAGE_CATEGORY}
              listName="Bra Daililing brand landing catalog"
              anchorId="katalog-bra-daililing"
              theme={THEME}
              searchPlaceholder="Cari bra Daililing berdasarkan nama atau nomor model"
              emptyMessage="Model tidak ditemukan."
            />
          </div>
        </section>

        <section className="bg-[#f0fff8] py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <SectionLabel>Pesan melalui Dropify</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kenapa toko memesan bra Daililing di Dropify?</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Satu admin membantu mengecek nama model, foto, ukuran, warna, jumlah, harga, dan pengiriman.</p>
              <WhatsAppButton message={heroMessage} label="Chat admin bra Daililing" placement="why_dropify" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {DROPIFY_REASONS.map((item) => (
                <article key={item.title} className="rounded-2xl border border-emerald-100 bg-white p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#103d20] text-emerald-200">
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-emerald-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <SectionLabel>FAQ Bra Daililing</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Yang sering ditanyakan pembeli grosir</h2>
            </div>
            <div className="mt-9 divide-y divide-emerald-100 border-y border-emerald-100">
              {BRA_DAILILING_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-[#102b18]">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-emerald-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-emerald-200 py-14 text-[#103d20]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Siapkan daftar model</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim foto atau nama model, jumlah lusin, dan kota tujuan.</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">Tambahkan kebutuhan ukuran dan warna agar pengecekan stok pertama lebih lengkap.</p>
            </div>
            <WhatsAppButton message={heroMessage} label="Minta quotation bra Daililing" placement="final_cta" className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#103d20] px-7 py-4 text-base font-black text-white transition-colors hover:bg-[#0b2d18]" />
          </div>
        </section>

        <BrandTrustSections brandName="Daililing" itemName="bra Daililing" images={proofImages} questions={BRA_DAILILING_TRUST_QUESTIONS} />
        <LpCrossLinks currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
